import './pbmPromotion.scss';

import { useState } from 'react';
import { Badge, Button, Modal } from 'react-bootstrap';
import { FaCartPlus } from 'react-icons/fa';
import { IoAlertCircleOutline } from 'react-icons/io5';
import { MdDiscount } from 'react-icons/md';
import { toast } from 'react-toastify';

import { useOrderContext } from '@/resources/hooks/useOrderContext';
import { useTextColor } from '@/resources/hooks/useTextColor';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { changeItem } from '@/routes/order';
import { applyPbmPromotion } from '@/routes/products';
import { ICartProduct } from '@/store/ducks/order/types';
import { IAlert, IProduct } from '@/store/ducks/products/types';

import { BootstrapTable, RowType } from '../../Generics/BootstrapTable';
import Load from '../../global/Load';
import MoneyDisplay from '../../global/MoneyDisplay/MoneyDisplay';

interface PbmPromotionsModalProps {
  show?: boolean;
  onHide?: () => void;
  promotions: IAlert[];
  isCart?: boolean;
  color: string;
  product: IProduct | ICartProduct;
}

export function PbmPromotionsModal({
  show,
  onHide,
  promotions,
  isCart = false,
  color,
  product,
}: PbmPromotionsModalProps) {
  const { order } = useWebContext();
  const { useFunction } = useOrderContext();
  const [loading, setLoading] = useState(false);
  const setSelectedPromotion = useFunction('setSelectedPromotion');

  const fontColor = useTextColor(color);

  const validatePromotions = promotions.filter(
    (promotion) =>
      !!promotion.price && (!!promotion.max_discount_percentage || !!promotion.discount),
  );

  const handlePromotionClick = (promotion: IAlert) => {
    setSelectedPromotion?.(promotion);
    setLoading(true);

    applyPbmPromotion(order?.id, product.ean, promotion.quantity)
      .catch((e) => {
        console.error(e);
        toast(e?.response?.data?.message || 'Erro ao aplicar promoção', {
          type: e?.response?.data?.type || 'error',
        });
      })
      .finally(() => {
        setLoading(false);
        onHide?.();
      });
  };

  const handleRemovePromotion = () => {
    changeItem(
      order?.id as number,
      {
        product_id: product?.product_id as number,
        quantity: product?.quantity,
        ean: product?.ean as string,
        uid: product?.uid as string,
        options: [],
      },
      false,
      true,
    ).finally(() => {
      onHide?.();
    });
  };

  const columns = [
    {
      title: 'Quantidade',
      titleClassName: 'text-center',
      columnClassName: 'text-center',
      dataIndex: 'quantity',
      render: (row: RowType) => <span>{row.quantity} Un</span>,
    },
    {
      title: 'Desconto',
      titleClassName: 'text-center',
      columnClassName: 'text-center',
      dataIndex: 'discount',
      render: (row: RowType) => (
        <Badge
          className="p-2 px-3 rounded-pill"
          style={{
            backgroundColor: color,
            color: fontColor,
          }}
        >
          {parseFloat(`${row.discount}`).toFixed(0)}%
        </Badge>
      ),
    },
    {
      title: 'Valor unitário',
      titleClassName: 'text-center',
      columnClassName: 'text-center',
      dataIndex: 'gross_price',
      render: (row: RowType) => (
        <MoneyDisplay
          numberStyle={{ fontWeight: 'initial' }}
          symbolStyle={{ fontWeight: 'initial' }}
          bold={false}
          value={Number(row.gross_price)}
        />
      ),
    },
    {
      title: 'Valor Final',
      titleClassName: 'text-center',
      columnClassName: 'text-center',
      render: (row: RowType) => (
        <MoneyDisplay
          numberStyle={{ fontWeight: 'initial' }}
          symbolStyle={{ fontWeight: 'initial' }}
          value={Number(row.price ?? 0) * Number(row.quantity ?? 0)}
        />
      ),
    },
    {
      title: 'Ação',
      columnClassName: 'text-center',
      titleClassName: 'text-center',
      render: (row: RowType) => (
        <Button
          variant="primary"
          className="px-1 py-0"
          onClick={() => handlePromotionClick(row as unknown as IAlert)}
          disabled={loading}
        >
          <FaCartPlus size="0.8em" />
        </Button>
      ),
    },
  ];

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="modal-rounded">
      <Modal.Header closeButton>
        <Modal.Title>
          <span style={{ color }} className="pr-2">
            <MdDiscount />
          </span>{' '}
          <strong>Descontos do laboratório</strong>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="discount-modal-body">
        <div className="d-flex flex-column gap-3">
          <span className="d-flex flex-row gap-2 align-items-center">
            <div
              className="text-truncate"
              style={{
                maxWidth: '50ch',
                fontWeight: '800',
              }}
            >
              {product.title}
            </div>
            <span>
              ({product.ean}) - {product.brand || promotions[0]?.laboratory_name}
            </span>
          </span>
        </div>

        {loading && <Load color="primary" />}
        <div>
          {!loading && validatePromotions.length > 0 && (
            <BootstrapTable
              data={validatePromotions}
              columns={columns}
              maxHeight={300}
              pagination={{ type: 'scroll', totalPerPage: 5 }}
            />
          )}
        </div>

        <div className="d-flex flex-column py-2">
          <strong>Regulamento: </strong>
          <span className="align-items-center">
            <IoAlertCircleOutline size={16} color="#d37a20" className="mb-1" /> O valor do desconto
            pode mudar de acordo com os itens do pedido, o CPF cadastrado ou a forma de pagamento
            escolhida.
          </span>
        </div>
        {isCart && !loading && (
          <Button variant="secondary" onClick={handleRemovePromotion} className="mt-3">
            Remover desconto
          </Button>
        )}
      </Modal.Body>
    </Modal>
  );
}
